import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Vault, ShieldCheck, Languages, Gem, HandCoins, BellRing, BarChart3, Calculator, Check, LayoutDashboard, Users, Banknote, Landmark, Gavel, Warehouse, Activity, FileText, Settings } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money } from "../lib/api";
import { useLang } from "../lib/i18n";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { ContactDrop } from "../components/ContactDrop";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const HERO_IMG = "https://images.unsplash.com/photo-1611107683227-e9060eccd846?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxnb2xkJTIwamV3ZWxyeSUyMHBhd24lMjBzaG9wJTIwbG9hbiUyMGZpbmFuY2V8ZW58MHx8fHwxNzg5MTE3NzY2fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [registerOpen, setRegisterOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [calc, setCalc] = useState({ principal: 500, rate: 5, months: 3 });
  const [form, setForm] = useState({ shop_name: "", slug: "", owner_name: "", email: "", password: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("gp_visit")) return;
      const p = new URLSearchParams(window.location.search);
      API.post("/public/track-visit", {
        path: window.location.pathname,
        referrer: document.referrer || "",
        utm_source: p.get("utm_source") || "",
        utm_medium: p.get("utm_medium") || "",
        utm_campaign: p.get("utm_campaign") || "",
      }).catch(() => {});
      sessionStorage.setItem("gp_visit", "1");
    } catch (e) { /* ignore */ }
  }, []);

  const calcOut = useMemo(() => {
    const interest = (calc.principal * calc.rate * calc.months) / 100;
    return { interest, total: Number(calc.principal) + interest };
  }, [calc]);

  const openRegister = (plan) => {
    setSelectedPlan(plan);
    setResult(null);
    setRegisterOpen(true);
  };

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 30);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await API.post("/public/register", { ...form, plan_id: selectedPlan.id, billing_cycle: cycle });
      setResult(data);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const features = [
    { icon: HandCoins, title: t("nav.loans"), desc: t("loans.redeem_hint") },
    { icon: Gem, title: t("nav.items"), desc: t("landing.trust1") },
    { icon: BellRing, title: t("nav.alerts"), desc: t("alerts.title") },
    { icon: BarChart3, title: t("nav.reports"), desc: t("reports.portfolio") },
    { icon: ShieldCheck, title: t("landing.trust2"), desc: t("settings.pending_text") },
    { icon: Languages, title: t("landing.trust3"), desc: "English / Tetum" },
  ];

  const modules = [
    { icon: LayoutDashboard, name: t("nav.dashboard"), img: "dashboard" },
    { icon: Users, name: t("nav.customers"), img: "customers" },
    { icon: Banknote, name: t("nav.repayments"), img: "repayments" },
    { icon: Landmark, name: t("nav.finance"), img: "finance" },
    { icon: Gavel, name: t("nav.auctions"), img: "auctions" },
    { icon: Warehouse, name: t("nav.warehouse"), img: "warehouse" },
    { icon: Activity, name: t("nav.audit"), img: "activity" },
    { icon: FileText, name: t("landing.mod_contracts"), img: "contracts" },
    { icon: Settings, name: t("nav.settings"), img: "settings" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-x-hidden" data-testid="landing-page">
      <div className="bg-[#111111] text-slate-300 text-xs" data-testid="kinos-contact-bar">
        <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-center sm:justify-end flex-wrap gap-x-5 gap-y-1 font-mono">
          <a href="https://www.kinos.tl" target="_blank" rel="noreferrer" className="hover:text-amber-400 transition-colors" data-testid="kinos-website">www.kinos.tl</a>
          <a href="tel:+6703311480" className="hover:text-amber-400 transition-colors" data-testid="kinos-phone">+670 3311 480 / 7713 8966</a>
          <span className="hidden md:inline" data-testid="kinos-address">Rua Fatuhada, Comoro, Dom-Aleixo, Dili, Timor-Leste</span>
        </div>
      </div>
      <header className="sticky top-0 z-40 glass-topbar border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Vault className="h-7 w-7 text-red-600" />
            <span className="font-heading font-bold text-xl text-slate-900" data-testid="brand-name">{t("brand")}</span>
            <span className="hidden sm:inline-flex items-center text-[10px] font-mono uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid="brand-country">Timor-Leste</span>
            <span className="hidden sm:block h-6 w-px bg-slate-300 mx-1" />
            <a href="https://www.kinos.tl" target="_blank" rel="noreferrer" className="hidden sm:block hover:opacity-90 transition-opacity" title="KINOS Leading Innovation Technology">
              <img src="/kinos-logo.png" alt="KINOS Leading Innovation Technology" className="h-12 w-auto" data-testid="kinos-logo-header" />
            </a>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login">
              <Button variant="outline" data-testid="landing-signin-button">{t("landing.sign_in")}</Button>
            </Link>
            <Button data-testid="landing-get-started-button" onClick={() => openRegister(plans[1] || plans[0])}
              className="bg-red-600 hover:bg-red-700 text-white hidden sm:inline-flex">
              {t("landing.get_started")}
            </Button>
          </div>
        </div>
      </header>

      <section className="bg-[#111111] hero-grid-bg text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="animate-fade-up min-w-0">
            <div className="inline-block text-xs font-mono uppercase tracking-widest text-amber-400 border border-amber-500/40 rounded-full px-3 py-1 mb-6">
              {t("landing.tagline")}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none">
              {t("landing.hero_title")}
            </h1>
            <p className="mt-4 font-heading text-lg sm:text-xl font-semibold text-amber-400" data-testid="hero-brand-line">
              Gestão Penhor <span className="text-slate-300 font-normal">— Timor-Leste</span>
            </p>
            <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-lg">{t("landing.hero_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-get-started-button" onClick={() => openRegister(plans[1] || plans[0])}
                className="bg-red-600 hover:bg-red-700 text-white h-12 px-6">
                {t("landing.get_started")}
              </Button>
              <a href="#pricing">
                <Button variant="outline" className="h-12 px-6 border-slate-600 text-slate-200 hover:bg-slate-800" data-testid="hero-pricing-button">
                  {t("landing.pricing_title")}
                </Button>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-amber-400" />{t("landing.trust1")}</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-amber-400" />{t("landing.trust2")}</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-amber-400" />{t("landing.trust3")}</span>
            </div>
          </div>
          <div className="flex justify-center lg:absolute lg:left-[43%] lg:-translate-x-1/2 lg:bottom-0 lg:z-10 pointer-events-none animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <img src="/dely-web.png" alt="KINOS specialist" data-testid="hero-person-img"
              className="h-64 sm:h-80 lg:h-[540px] w-auto drop-shadow-2xl" />
          </div>
          <div className="relative animate-fade-up min-w-0" style={{ animationDelay: "0.15s" }}>
            <div className="relative mx-auto w-[320px] h-[320px] sm:w-[380px] sm:h-[380px] rounded-full overflow-hidden bg-white shadow-2xl border border-slate-200" data-testid="hero-gold-disc">
              <img src={HERO_IMG} alt="Gold collateral" data-testid="hero-gold-img" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-20 -bottom-2 sm:absolute sm:-bottom-6 sm:right-2 mt-4 sm:mt-0 bg-white text-slate-900 rounded-xl shadow-xl p-5 w-full sm:w-72 border border-slate-200">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">
                <Calculator className="h-4 w-4 text-red-600" />{t("landing.calc_title")}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500">{t("loans.principal")}</label>
                  <Input type="number" data-testid="calc-principal-input" value={calc.principal}
                    onChange={(e) => setCalc({ ...calc, principal: +e.target.value })} className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500">%/{t("landing.monthly").slice(0, 3)}</label>
                  <Input type="number" data-testid="calc-rate-input" value={calc.rate}
                    onChange={(e) => setCalc({ ...calc, rate: +e.target.value })} className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500">{t("loans.term").split(" ")[0]}</label>
                  <Input type="number" data-testid="calc-months-input" value={calc.months}
                    onChange={(e) => setCalc({ ...calc, months: +e.target.value })} className="h-8 text-sm" />
                </div>
              </div>
              <div className="mt-3 flex justify-between text-sm">
                <span className="text-slate-500">{t("reports.interest_earned")}</span>
                <span className="font-mono font-semibold text-amber-600" data-testid="calc-interest-output">{money(calcOut.interest)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">{t("loans.total_due")}</span>
                <span className="font-mono font-bold text-red-700" data-testid="calc-total-output">{money(calcOut.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900" data-testid="features-title">
          {t("landing.features_title")}
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} data-testid={`feature-card-${i}`}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:border-red-400 hover:shadow-lg transition-all">
              <f.icon className="h-8 w-8 text-red-600" />
              <h3 className="font-heading text-lg font-medium mt-4 text-slate-900">{f.title}</h3>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="modules-grid">
          {modules.map((m, i) => (
            <div key={i} data-testid={`module-card-${i}`} onClick={() => setPreview(m)}
              className="relative group cursor-pointer bg-slate-50 rounded-lg border border-slate-200 px-4 py-3 flex items-center gap-2.5 hover:border-red-400 hover:bg-white transition-all">
              <m.icon className="h-4 w-4 shrink-0 text-red-600" />
              <span className="text-sm font-medium text-slate-800">{m.name}</span>
              <img src={`/modules/${m.img}.jpeg`} alt="" loading="lazy" data-testid={`module-preview-${i}`}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-[480px] max-w-[85vw] rounded-xl border border-slate-200 shadow-2xl opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 z-50 bg-white hidden sm:block" />
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900" data-testid="pricing-title">
              {t("landing.pricing_title")}
            </h2>
            <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1" data-testid="billing-toggle">
              <button onClick={() => setCycle("monthly")} data-testid="billing-monthly-button"
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${cycle === "monthly" ? "bg-[#111111] text-white" : "text-slate-600"}`}>
                {t("landing.monthly")}
              </button>
              <button onClick={() => setCycle("annual")} data-testid="billing-annual-button"
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${cycle === "annual" ? "bg-[#111111] text-white" : "text-slate-600"}`}>
                {t("landing.annual")} <span className="text-amber-500 text-xs">{t("landing.save20")}</span>
              </button>
            </div>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {plans.map((p) => {
              const price = cycle === "annual" ? p.price_annual : p.price_monthly;
              const popular = p.key === "professional";
              return (
                <div key={p.id} data-testid={`plan-card-${p.key}`}
                  className={`relative rounded-2xl p-8 border ${popular ? "bg-[#111111] text-white border-slate-700 shadow-2xl" : "bg-white border-slate-200"}`}>
                  {popular && (
                    <span className="absolute -top-3 left-6 bg-amber-500 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">Popular</span>
                  )}
                  <h3 className="font-heading text-xl font-semibold">{p.name}</h3>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-bold" data-testid={`plan-price-${p.key}`}>{money(price)}</span>
                    <span className={`text-sm ${popular ? "text-slate-400" : "text-slate-500"}`}>
                      {cycle === "annual" ? t("landing.per_year") : t("landing.per_month")}
                    </span>
                  </div>
                  <ul className="mt-6 space-y-2.5">
                    {(p.features || []).map((f, i) => (
                      <li key={i} className={`flex items-start gap-2 text-sm ${popular ? "text-slate-300" : "text-slate-600"}`}>
                        <Check className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />{f}
                      </li>
                    ))}
                  </ul>
                  <Button data-testid={`choose-plan-${p.key}`} onClick={() => openRegister(p)}
                    className={`w-full mt-8 h-11 ${popular ? "bg-red-600 hover:bg-red-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                    {t("landing.choose_plan")}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="bg-[#111111] text-slate-400 text-sm">
        <div className="max-w-6xl mx-auto px-6 py-10 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Vault className="h-5 w-5 text-amber-400" />
            <span className="font-heading font-semibold text-white">{t("brand")}</span>
          </div>
          <div className="flex items-center gap-3" data-testid="kinos-footer">
            <span className="font-mono text-xs">© 2026 {t("brand")} SaaS</span>
            <span className="hidden sm:inline text-slate-600">·</span>
            <span className="text-xs text-slate-500">
              Powered by <a href="https://www.kinos.tl" target="_blank" rel="noreferrer" className="text-slate-300 hover:text-amber-400 transition-colors font-semibold" data-testid="kinos-footer-link">KINOS Leading Innovation Technology</a>
            </span>
          </div>
        </div>
      </footer>

      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="register-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {result ? t("landing.bank_title") : t("landing.register_title")}
            </DialogTitle>
          </DialogHeader>
          {!result ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>{t("landing.shop_name")}</Label>
                <Input data-testid="register-shop-name-input" required value={form.shop_name}
                  onChange={(e) => setForm({ ...form, shop_name: e.target.value, slug: form.slug || slugify(e.target.value) })} className="mt-1" />
              </div>
              <div>
                <Label>{t("landing.shop_id")}</Label>
                <Input data-testid="register-slug-input" required value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="mt-1 font-mono" />
                <p className="text-xs text-slate-500 mt-1">{form.slug ? `${form.slug}.pawno.app` : "your-shop.pawno.app"}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("landing.owner_name")}</Label>
                  <Input data-testid="register-owner-name-input" required value={form.owner_name}
                    onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("landing.email")}</Label>
                  <Input data-testid="register-email-input" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
                </div>
              </div>
              <div>
                <Label>{t("landing.password")}</Label>
                <Input data-testid="register-password-input" type="password" required minLength={6} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("landing.plan")}</Label>
                  <Select value={selectedPlan?.id || ""} onValueChange={(v) => setSelectedPlan(plans.find((p) => p.id === v))}>
                    <SelectTrigger data-testid="register-plan-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {plans.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`register-plan-option-${p.key}`}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("landing.billing")}</Label>
                  <Select value={cycle} onValueChange={setCycle}>
                    <SelectTrigger data-testid="register-cycle-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="monthly" data-testid="cycle-option-monthly">{t("landing.monthly")}</SelectItem>
                      <SelectItem value="annual" data-testid="cycle-option-annual">{t("landing.annual")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedPlan && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm flex justify-between">
                  <span className="text-amber-800">{selectedPlan.name} · {t(`landing.${cycle}`)}</span>
                  <span className="font-mono font-bold text-amber-900" data-testid="register-amount-label">
                    {money(cycle === "annual" ? selectedPlan.price_annual : selectedPlan.price_monthly)}
                  </span>
                </div>
              )}
              <p data-testid="register-local-pay-note" className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
                {t("settings.local_pay_note")}
              </p>
              <ContactDrop />
              <Button data-testid="register-submit-button" type="submit" disabled={busy || !selectedPlan}
                className="w-full bg-red-600 hover:bg-red-700 text-white h-11">
                {busy ? t("common.loading") : t("landing.submit")}
              </Button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="bank-instructions-panel">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                {result.bank?.instructions}
              </div>
              {(result.bank_accounts?.length ? result.bank_accounts : [result.bank]).map((acc, i) => (
                <div key={acc?.id || i} className="rounded-lg border border-slate-200 divide-y text-sm" data-testid={`bank-account-panel-${i}`}>
                  <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.name")}</span><span className="font-medium">{acc?.bank_name}</span></div>
                  <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.acc_name")}</span><span className="font-medium">{acc?.account_name}</span></div>
                  <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.acc_no")}</span><span className="font-mono font-medium">{acc?.account_number}</span></div>
                  {acc?.iban && <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.iban")}</span><span className="font-mono font-medium">{acc.iban}</span></div>}
                  {acc?.swift && <div className="flex justify-between p-3"><span className="text-slate-500">{t("bank.swift")}</span><span className="font-mono font-medium">{acc.swift}</span></div>}
                </div>
              ))}
              <div className="rounded-lg border border-amber-200 divide-y text-sm">
                <div className="flex justify-between p-3 bg-amber-50"><span className="text-amber-800">{t("landing.reference")}</span><span className="font-mono font-bold text-amber-900" data-testid="payment-reference-code">{result.reference}</span></div>
                <div className="flex justify-between p-3 bg-amber-50"><span className="text-amber-800">{t("landing.amount_due")}</span><span className="font-mono font-bold text-amber-900">{money(result.amount)}</span></div>
              </div>
              <p data-testid="bank-instructions-local-pay-note" className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
                {t("settings.local_pay_note")}
              </p>
              <ContactDrop />
              <Button data-testid="goto-login-button" onClick={() => navigate(`/login?shop=${result.shop_slug}`)}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">
                {t("landing.goto_login")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-white max-w-2xl p-4" data-testid="module-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {preview && <><preview.icon className="h-5 w-5 text-red-600" />{preview.name}</>}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <img src={`/modules/${preview.img}.jpeg`} alt={preview.name} data-testid="module-preview-full"
              className="w-full rounded-lg border border-slate-200" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
